import type { LucideIcon } from "lucide-react";
import { MoreHorizontal } from "lucide-react";
import { useAnchoredMenu } from "./useAnchoredMenu";

export interface ActionMenuItem {
  label: string;
  onClick: () => void;
  icon?: LucideIcon;
  /** Small grey text after the label, e.g. an amount due */
  hint?: string;
  danger?: boolean;
  /** Draw a divider above this item */
  separated?: boolean;
}

interface ActionMenuProps {
  items: ActionMenuItem[];
  /** Accessible name of the ⋯ button */
  label?: string;
  className?: string;
}

/** "⋯" button with a small menu of row actions. */
export default function ActionMenu({ items, label = "More actions", className = "" }: ActionMenuProps) {
  const { open, setOpen, toggle, anchorRef, menuRef, style } = useAnchoredMenu(items.length * 42 + 12);
  if (items.length === 0) return null;

  return (
    <>
      <button
        ref={anchorRef}
        type="button"
        onClick={(e) => { e.stopPropagation(); toggle(); }}
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        className={`h-10 w-10 md:h-8 md:w-8 inline-flex items-center justify-center rounded-lg border border-stone-200 bg-white text-stone-600 hover:bg-stone-100 transition-colors ${className}`}
      >
        <MoreHorizontal className="w-4 h-4" />
      </button>
      {open && (
        <div
          ref={menuRef}
          role="menu"
          style={style}
          onClick={(e) => e.stopPropagation()}
          className="min-w-[200px] bg-white border border-stone-200 rounded-xl shadow-xl p-1"
        >
          {items.map((item, i) => {
            const Icon = item.icon;
            return (
              <div key={item.label}>
                {item.separated && i > 0 && <div className="my-1 border-t border-stone-100" />}
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => { setOpen(false); item.onClick(); }}
                  className={`w-full min-h-10 md:min-h-9 px-3 rounded-lg flex items-center gap-2.5 text-sm text-left transition-colors ${
                    item.danger ? "text-red-600 hover:bg-red-50" : "text-stone-700 hover:bg-amber-50"
                  }`}
                >
                  {Icon && <Icon className="w-4 h-4 shrink-0" />}
                  <span className="flex-1">{item.label}</span>
                  {item.hint && <span className="text-xs text-stone-400 tabular-nums">{item.hint}</span>}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}
